import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Search,
  Download,
  Share2,
  SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEvents } from '@context/EventContext';
import { useCart } from '@context/CartContext';
import { useDebounce } from '@hooks/useDebounce';
import { formatDate, generatePageTitle } from '@utils/helpers';
import { ROUTES } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import LoadingSpinner from '@components/common/LoadingSpinner';
import Modal from '@components/common/Modal';
import PhotoGrid from '@components/gallery/PhotoGrid';
import PhotoModal from '@components/gallery/PhotoModal';
import type { Photo } from '@/types';

interface FilterState {
  sortBy: 'date' | 'title' | 'size';
  sortOrder: 'asc' | 'desc';
  selectedTags: string[];
  dateFrom: string;
  dateTo: string;
}

const defaultFilters: FilterState = {
  sortBy: 'date',
  sortOrder: 'desc',
  selectedTags: [],
  dateFrom: '',
  dateTo: '',
};

const EventGalleryPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [isDownloading, setIsDownloading] = useState(false);

  const { currentEvent, photos, loading, error, fetchEvent, fetchPhotos } = useEvents();
  const { items: cartItems } = useCart();

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Load event and photos
  useEffect(() => {
    if (!eventId) return;

    const loadEventData = async () => {
      try {
        await fetchEvent(eventId);
        await fetchPhotos(eventId);
      } catch {
        // Error handled via UI state
      }
    };

    loadEventData();
  }, [eventId, fetchEvent, fetchPhotos]);

  // Update document title
  useEffect(() => {
    if (currentEvent) {
      document.title = generatePageTitle(`${currentEvent.title} - Gallery`);
    }

    return () => {
      document.title = generatePageTitle();
    };
  }, [currentEvent]);

  // Get all unique tags from photos
  const allTags = React.useMemo(() => {
    const tagSet = new Set<string>();
    photos.forEach(photo => {
      photo.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [photos]);

  // Filter and sort photos
  const filteredPhotos = React.useMemo(() => {
    let result = [...photos];

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        photo =>
          photo.title?.toLowerCase().includes(searchLower) ||
          photo.description?.toLowerCase().includes(searchLower) ||
          photo.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Apply tag filter
    if (filters.selectedTags.length > 0) {
      result = result.filter(photo =>
        filters.selectedTags.some(tag => photo.tags?.includes(tag))
      );
    }

    // Apply date filters
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      result = result.filter(photo => new Date(photo.createdAt) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter(photo => new Date(photo.createdAt) <= toDate);
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'size':
          comparison = (a.metadata?.fileSize || 0) - (b.metadata?.fileSize || 0);
          break;
      }
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [photos, debouncedSearch, filters]);

  // Check if filters are active
  const hasActiveFilters = React.useMemo(() => {
    return (
      filters.sortBy !== defaultFilters.sortBy ||
      filters.sortOrder !== defaultFilters.sortOrder ||
      filters.selectedTags.length > 0 ||
      filters.dateFrom !== '' ||
      filters.dateTo !== ''
    );
  }, [filters]);

  const handlePhotoSelect = useCallback((photo: Photo) => {
    setSelectedPhoto(photo);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPhoto(null);
  }, []);

  const handleNextPhoto = useCallback(() => {
    if (!selectedPhoto) return;

    const currentIndex = filteredPhotos.findIndex(p => p.id === selectedPhoto.id);
    const nextIndex = (currentIndex + 1) % filteredPhotos.length;
    setSelectedPhoto(filteredPhotos[nextIndex]);
  }, [selectedPhoto, filteredPhotos]);

  const handlePreviousPhoto = useCallback(() => {
    if (!selectedPhoto) return;

    const currentIndex = filteredPhotos.findIndex(p => p.id === selectedPhoto.id);
    const previousIndex = currentIndex === 0 ? filteredPhotos.length - 1 : currentIndex - 1;
    setSelectedPhoto(filteredPhotos[previousIndex]);
  }, [selectedPhoto, filteredPhotos]);

  const handleShare = async () => {
    if (!currentEvent) return;

    const shareData = {
      title: `${currentEvent.title} - Photo Gallery`,
      text: `Check out photos from ${currentEvent.title}`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // Handle share error or fallback to clipboard
        navigator.clipboard.writeText(window.location.href);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  const handleBulkDownload = async () => {
    if (filteredPhotos.length === 0) {
      toast.error('No photos to download');
      return;
    }

    setIsDownloading(true);
    toast.info(`Preparing ${filteredPhotos.length} photos for download...`);

    try {
      // For now, this will download purchased photos or trigger a checkout flow
      // In production, this would use a backend service to create a zip archive
      const purchasedPhotos = filteredPhotos.filter(photo =>
        cartItems.some(item => item.photoId === photo.id)
      );

      if (purchasedPhotos.length === 0) {
        toast.info('Add photos to your cart and complete purchase to download them');
        return;
      }

      // Simulate bulk download preparation
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success(`Download started for ${purchasedPhotos.length} photos`);

      // In production, this would trigger actual downloads
      // For now, we show a success message
    } catch (error) {
      toast.error('Failed to prepare download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
  };

  const toggleTag = (tag: string) => {
    setFilters(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.includes(tag)
        ? prev.selectedTags.filter(t => t !== tag)
        : [...prev.selectedTags, tag],
    }));
  };

  if (!eventId) {
    navigate(ROUTES.EVENTS);
    return null;
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate(ROUTES.EVENTS)}>Back to Events</Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading && !currentEvent) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center">
            <div className="text-center">
              <LoadingSpinner size="large" />
              <p className="text-gray-500 mt-4">Loading event...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.EVENTS)}
            icon={<ArrowLeft size={16} />}
            className="text-gray-600 hover:text-gray-900"
          >
            Back to Events
          </Button>
        </div>

        {/* Event Header */}
        {currentEvent && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1 mb-4 lg:mb-0">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{currentEvent.title}</h1>

                {currentEvent.description && (
                  <p className="text-lg text-gray-600 mb-4">{currentEvent.description}</p>
                )}

                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar size={16} />
                    <span>{formatDate(currentEvent.eventDate)}</span>
                  </div>
                  {currentEvent.location && (
                    <div className="flex items-center gap-1">
                      <MapPin size={16} />
                      <span>{currentEvent.location}</span>
                    </div>
                  )}
                  <div className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-xs font-medium">
                    {currentEvent.totalPhotos} photos
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleShare} icon={<Share2 size={16} />}>
                  Share
                </Button>
                <Button
                  variant="outline"
                  icon={<Download size={16} />}
                  onClick={handleBulkDownload}
                  loading={isDownloading}
                  disabled={filteredPhotos.length === 0}
                >
                  Download All
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search photos..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                leftIcon={<Search size={16} />}
              />
            </div>

            <Button
              variant={hasActiveFilters ? 'primary' : 'outline'}
              icon={<SlidersHorizontal size={16} />}
              onClick={() => setIsFilterModalOpen(true)}
            >
              Filter{hasActiveFilters ? ` (${filters.selectedTags.length + (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0)})` : ''}
            </Button>
          </div>

          {debouncedSearch && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-gray-500">
                Searching for "{debouncedSearch}" • {filteredPhotos.length} result
                {filteredPhotos.length !== 1 ? 's' : ''}
              </span>
              <Button variant="ghost" size="small" onClick={() => setSearchTerm('')}>
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Photo Grid */}
        <PhotoGrid
          photos={filteredPhotos}
          loading={loading}
          onPhotoSelect={handlePhotoSelect}
          selectable={true}
        />

        {/* Load more button */}
        {!loading && filteredPhotos.length > 0 && filteredPhotos.length % 50 === 0 && (
          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() =>
                eventId && fetchPhotos(eventId, Math.floor(filteredPhotos.length / 50) + 1)
              }
              loading={loading}
            >
              Load More Photos
            </Button>
          </div>
        )}

        {/* Photo Modal */}
        <PhotoModal
          photo={selectedPhoto}
          photos={filteredPhotos}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onNext={handleNextPhoto}
          onPrevious={handlePreviousPhoto}
        />

        {/* Filter Modal */}
        <Modal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          title="Filter Photos"
          size="medium"
        >
          <div className="space-y-6">
            {/* Sort Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <div className="flex gap-2">
                <select
                  value={filters.sortBy}
                  onChange={e => handleFilterChange({ sortBy: e.target.value as FilterState['sortBy'] })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="date">Date</option>
                  <option value="title">Title</option>
                  <option value="size">File Size</option>
                </select>
                <select
                  value={filters.sortOrder}
                  onChange={e => handleFilterChange({ sortOrder: e.target.value as FilterState['sortOrder'] })}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={e => handleFilterChange({ dateFrom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={e => handleFilterChange({ dateTo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Tags */}
            {allTags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        filters.selectedTags.includes(tag)
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="ghost" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                Clear All
              </Button>
              <Button variant="primary" onClick={() => setIsFilterModalOpen(false)}>
                Apply Filters
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default EventGalleryPage;
